import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ArrowDownTrayIcon,
  CheckIcon,
  ClipboardDocumentCheckIcon,
  EyeIcon,
  MinusIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { fetchApplicationFormPdf } from './ApplicationFormModal';
import PdfPreviewModal, { saveBlob } from './PdfPreviewModal';

/**
 * Interview result for Application Form screenings (Ajanta), as one card read top to bottom:
 * verdict (score, 1–5 band; red flags are not shown, they read as alarming to the panel) → score
 * by area → what stood out → interview summary (what was good and what was not, on the points the
 * panel hires on). Form ratings come from the server (aiRatings.js), so screen and PDF always
 * show the same numbers for the same round (`sessionId`).
 */

const SCALE = {
  5: { label: 'Excellent', text: 'text-emerald-700', bar: 'bg-emerald-500', ring: '#059669' },
  4: { label: 'Good', text: 'text-teal-700', bar: 'bg-teal-500', ring: '#0d9488' },
  3: { label: 'Fair', text: 'text-amber-700', bar: 'bg-amber-400', ring: '#d97706' },
  2: { label: 'Average', text: 'text-orange-700', bar: 'bg-orange-500', ring: '#ea580c' },
  1: { label: 'Poor', text: 'text-red-700', bar: 'bg-red-500', ring: '#dc2626' },
};

// Same bands as toFivePoint in server/modules/application-form/aiRatings.js.
export const fivePoint = (score) =>
  typeof score !== 'number' || Number.isNaN(score) ? null : score >= 90 ? 5 : score >= 75 ? 4 : score >= 60 ? 3 : score >= 40 ? 2 : 1;

const sameName = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();

function ScoreRing({ score, rating }) {
  const color = SCALE[rating]?.ring || '#94a3b8';
  return (
    <div className="relative h-20 w-20 shrink-0">
      <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
        <circle cx="18" cy="18" r="15.5" fill="none" stroke="#eef2f7" strokeWidth="3" />
        <circle
          cx="18"
          cy="18"
          r="15.5"
          fill="none"
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={`${Math.min(score || 0, 100) * 0.974} 100`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className="text-2xl font-bold" style={{ color }}>
          {score ?? '—'}
        </span>
        <span className="mt-0.5 text-[10px] text-gray-400">of 100</span>
      </div>
    </div>
  );
}

function Verdict({ overall }) {
  const rating = fivePoint(overall);
  const band = SCALE[rating];
  return (
    <div className="flex min-w-0 items-center gap-4">
      <ScoreRing score={overall} rating={rating} />
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">AI interview result</p>
        {band ? (
          <p className="mt-0.5 flex flex-wrap items-baseline gap-x-2">
            <span className={`text-xl font-bold ${band.text}`}>{band.label}</span>
            <span className="text-sm text-gray-500">{rating} of 5 on the Assessment Form scale</span>
          </p>
        ) : (
          <p className="mt-0.5 text-sm text-gray-500">Not scored yet</p>
        )}
      </div>
    </div>
  );
}

function AreaRow({ area, formRating }) {
  const band = SCALE[fivePoint(area.score)];
  return (
    <li>
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate text-sm font-medium text-gray-800">{area.subject}</span>
        <span className="shrink-0 text-sm tabular-nums">
          <span className="font-semibold text-gray-900">{area.score}%</span>
          {band && <span className={`ml-2 text-xs font-medium ${band.text}`}>{band.label}</span>}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full ${band?.bar || 'bg-slate-400'}`}
          style={{ width: `${Math.max(0, Math.min(area.score, 100))}%` }}
        />
      </div>
      {formRating && (
        <p className="mt-1 text-xs text-gray-500">
          On the form as <span className="font-medium text-gray-700">{formRating.label}</span>: {formRating.rating}/5
        </p>
      )}
    </li>
  );
}

const HIGHLIGHTS = [
  ['strengths', CheckIcon, 'text-emerald-600 bg-emerald-50'],
  ['standout', StarIcon, 'text-indigo-600 bg-indigo-50'],
  ['improvements', MinusIcon, 'text-amber-600 bg-amber-50'],
];

function Highlights(insights) {
  const items = HIGHLIGHTS.flatMap(([key, Icon, tone]) => (insights[key] || []).map((text) => ({ key, text, Icon, tone })));
  if (!items.length) return <p className="text-sm italic text-gray-400">No highlights for this interview.</p>;
  return (
    <ul className="space-y-2">
      {items.map(({ key, text, Icon, tone }, i) => (
        <li key={`${key}-${i}`} className="flex gap-2.5 text-sm leading-snug text-gray-700">
          <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${tone}`}>
            <Icon className="h-3 w-3" strokeWidth={2.5} />
          </span>
          {text}
        </li>
      ))}
    </ul>
  );
}

const Heading = ({ children }) => (
  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">{children}</p>
);

function useAssessment(screeningId, candidateId, sessionId) {
  const [state, setState] = useState({ loading: true, error: '', data: null });
  useEffect(() => {
    if (!candidateId) return undefined;
    let cancelled = false;
    setState({ loading: true, error: '', data: null });
    axios
      .get(`/api/application-forms/screening/${screeningId}/candidates/${candidateId}/assessment`, {
        params: sessionId ? { sessionId } : undefined,
      })
      .then((res) => !cancelled && setState({ loading: false, error: '', data: res.data }))
      .catch(
        (err) =>
          !cancelled &&
          setState({ loading: false, error: err.response?.data?.message || 'Could not load the assessment scores.', data: null })
      );
    return () => {
      cancelled = true;
    };
  }, [screeningId, candidateId, sessionId]);
  return state;
}

function PdfButtons({ screeningId, candidateId, sessionId, candidateName, disabledReason }) {
  const [downloading, setDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const loadPdf = useCallback(
    () =>
      fetchApplicationFormPdf(screeningId, candidateId, {
        kind: 'assessment-pdf',
        sessionId,
        fallbackName: `Interview_Assessment_Form_${(candidateName || 'candidate').replace(/\s+/g, '_')}`,
      }),
    [screeningId, candidateId, sessionId, candidateName]
  );
  const download = async () => {
    setDownloading(true);
    try {
      const pdf = await loadPdf();
      saveBlob(pdf.blob, pdf.fileName);
    } catch {
      toast.error('Could not create the Assessment Form PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };
  const disabled = downloading || Boolean(disabledReason);
  return (
    <div className="flex shrink-0 items-center gap-2" title={disabledReason || undefined}>
      <button
        type="button"
        onClick={() => setPreviewOpen(true)}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50"
      >
        <EyeIcon className="h-4 w-4" />
        Preview PDF
      </button>
      <button
        type="button"
        onClick={download}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 rounded-lg bg-[#007BFF] px-3 py-2 text-sm font-medium text-white hover:bg-[#0056B3] disabled:opacity-50"
      >
        {downloading ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
        ) : (
          <ArrowDownTrayIcon className="h-4 w-4" />
        )}
        Download PDF
      </button>
      {previewOpen && (
        <PdfPreviewModal
          isOpen
          onClose={() => setPreviewOpen(false)}
          title="Interview Assessment Form"
          subtitle={candidateName}
          icon={<ClipboardDocumentCheckIcon className="h-5 w-5" />}
          load={loadPdf}
        />
      )}
    </div>
  );
}

/**
 * The summary saved when the interview was scored (interviewSummary.js), or null. Interviews
 * scored before it existed have none, and the card then shows no summary section.
 */
function useInterviewSummary(screeningId, candidateId, sessionId) {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    if (!candidateId) return undefined;
    let cancelled = false;
    setSummary(null);
    axios
      .get(`/api/application-forms/screening/${screeningId}/candidates/${candidateId}/assessment-summary`, {
        params: sessionId ? { sessionId } : undefined,
      })
      .then(({ data }) => !cancelled && (data?.strengths?.length || data?.concerns?.length) && setSummary(data))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [screeningId, candidateId, sessionId]);
  return summary;
}

const SUMMARY_COLUMNS = [
  ['strengths', 'What was good', CheckIcon, 'text-emerald-600 bg-emerald-50'],
  ['concerns', 'What was not', MinusIcon, 'text-amber-600 bg-amber-50'],
];

/** The interview in a few points on what the panel hires on (Sales Achievement, Experience, …). */
function InterviewSummary({ data }) {
  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      {SUMMARY_COLUMNS.map(([key, title, Icon, tone]) => (
        <div key={key}>
          <p className="mb-2 text-sm font-semibold text-gray-700">{title}</p>
          {data[key]?.length ? (
            <ul className="space-y-2">
              {data[key].map((p, i) => (
                <li key={`${key}-${i}`} className="flex gap-2.5 text-sm leading-snug text-gray-700">
                  <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${tone}`}>
                    <Icon className="h-3 w-3" strokeWidth={2.5} />
                  </span>
                  <span>
                    <span className="font-medium text-gray-900">{p.area}:</span> {p.point}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm italic text-gray-400">Nothing notable.</p>
          )}
        </div>
      ))}
    </div>
  );
}

export default function InterviewAssessmentCards({
  screeningId,
  candidateId,
  sessionId,
  candidateName,
  overall,
  areas: rawAreas = [],
  ...insights
}) {
  const areas = rawAreas.map((a) => ({ ...a, score: Math.round(a.score) }));
  const assessment = useAssessment(screeningId, candidateId, sessionId);
  const summary = useInterviewSummary(screeningId, candidateId, sessionId);
  const rated = assessment.data?.interviewCompleted ? assessment.data.parameters.filter((p) => p.rating != null) : [];
  const formRatingFor = (subject) => rated.find((p) => p.basedOn.some((name) => sameName(name, subject)));
  const disabledReason = !assessment.data
    ? 'Loading…'
    : !assessment.data.formStarted
      ? 'Available once the candidate has started the Application Form'
      : '';

  return (
    <section className="mt-2 overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:justify-between">
        <Verdict overall={overall} />
        {candidateId && (
          <PdfButtons
            screeningId={screeningId}
            candidateId={candidateId}
            sessionId={sessionId}
            candidateName={candidateName}
            disabledReason={disabledReason}
          />
        )}
      </div>

      <div className="grid grid-cols-1 border-t border-gray-100 md:grid-cols-2 md:divide-x md:divide-gray-100">
        <div className="p-5">
          <Heading>Score by area</Heading>
          {areas.length ? (
            <ul className="space-y-4">
              {areas.map((a) => (
                <AreaRow key={a.subject} area={a} formRating={formRatingFor(a.subject)} />
              ))}
            </ul>
          ) : (
            <p className="text-sm italic text-gray-400">No area scores for this interview.</p>
          )}
        </div>
        <div className="border-t border-gray-100 p-5 md:border-t-0">
          <Heading>What stood out</Heading>
          <Highlights {...insights} />
        </div>
      </div>

      {summary && (
        <div className="border-t border-gray-100 bg-slate-50/60 p-5">
          <Heading>Interview summary</Heading>
          <InterviewSummary data={summary} />
        </div>
      )}
    </section>
  );
}
